import dynamic from 'next/dynamic';

import type { ArticleNodeProps } from '@commons/pagebuilder/nodes/Article';
import type { CaseStudyNodeProps } from '@commons/pagebuilder/nodes/CaseStudy';
import type { EventNodeProps } from '@commons/pagebuilder/nodes/Event';
import type { PodcastNodeProps } from '@commons/pagebuilder/nodes/Podcast';
import type { PressReleaseNodeProps } from '@commons/pagebuilder/nodes/PressRelease';
import type { WebinarNodeProps } from '@commons/pagebuilder/nodes/Webinar';
import type { WhitePaperNodeProps } from '@commons/pagebuilder/nodes/WhitePaper';
import type { DrupalView } from '@commons/types/drupal';

import ByMemberDisplay from './ByMember';
import CardsDisplay from './Cards';
import FeaturedDisplay from './Featured';
import FeaturedTilesDisplay from './FeaturedTiles';
import LatestDisplay from './Latest';
import RelatedDisplay from './Related';

/**
 * Lazy loading the display to reduce the initial bundle size because it uses Swiper.
 *
 * This does not work when used in a pagebuilder factory directly, because every items are loaded at once.
 * Doing it in the component itself ensures that the chunks are only loaded when the component is rendered.
 *
 * @see https://nextjs.org/docs/app/building-your-application/optimizing/lazy-loading
 */

const LatestPopularDisplay = dynamic(() => import('./LatestPopular'));

/**
 * Enum of displays for the insights view.
 */

export enum INSIGHTS_VIEW_DISPLAY {
  Card = 'card_block',
  Featured = 'featured_block',
  FeaturedTile = 'featured_tile_block',
  Latest = 'latest_insights_block',
  LatestPopular = 'latest_popular_block',
  Related = 'related_insights_block',
  ByMember = 'insights_by_member_block',
}

/**
 * Union of node properties for the insights view.
 */

export type DrupalInsightNodeProps =
  | ArticleNodeProps
  | PressReleaseNodeProps
  | CaseStudyNodeProps
  | EventNodeProps
  | PodcastNodeProps
  | WebinarNodeProps
  | WhitePaperNodeProps;

/**
 * Type definition for the insights view filters.
 */

export type InsightsViewFilters = {
  popular: string[];
};

/**
 * Type definition for the insights view settings filters.
 */

export type InsightsViewSettingsFilters = {
  popular: string;
};

/**
 * Type definition for the insights view.
 */

export type DrupalInsightsView = DrupalView<
  DrupalInsightNodeProps,
  InsightsViewFilters,
  InsightsViewSettingsFilters
>;

/**
 * Insights view component.
 */

const InsightsView = (props: DrupalInsightsView) => {
  switch (props.display) {
    default:
    case INSIGHTS_VIEW_DISPLAY.Card:
      return <CardsDisplay {...props} />;
    case INSIGHTS_VIEW_DISPLAY.Featured:
      return <FeaturedDisplay {...props} />;
    case INSIGHTS_VIEW_DISPLAY.FeaturedTile:
      return <FeaturedTilesDisplay {...props} />;
    case INSIGHTS_VIEW_DISPLAY.Latest:
      return <LatestDisplay {...props} />;
    case INSIGHTS_VIEW_DISPLAY.LatestPopular:
      return <LatestPopularDisplay {...props} />;
    case INSIGHTS_VIEW_DISPLAY.Related:
      return <RelatedDisplay {...props} />;
    case INSIGHTS_VIEW_DISPLAY.ByMember:
      return <ByMemberDisplay {...props} />;
  }
};

export default InsightsView;
